import React, {
  useEffect,
  useState,
  useContext,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Switch,
  Route,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import useProjectById from '../../hooks/useProjectById';
import useProjectMediaFiles from '../../hooks/useProjectMediaFiles';
import useStageProgress from '../../hooks/useStageProgress';

import { appStore } from '../../state/app';
import { getMapping } from '../../utils/runtime-mappings';
import { getLastStageEndTime, isProjectOver } from '../../utils/helpers';

import RequestModal from '../../components/RequestModal/RequestModal';
import Loader from '../../components/Loader';
import ProjectView from './components/ProjectView';
import MonitoringData from './components/MonitoringData';
import ProjectFieldAgents from './components/ProjectFieldAgents/ProjectFieldAgents';
import ProjectHeader from './components/ProjectHeader';
import ProjectTabs from './components/ProjectTabs';
import CarbonPage from './components/CarbonPage/CarbonPage';

const ProjectPage = () => {
  const { state: { account } } = useContext(appStore);

  const translate = useIntl();
  const { path } = useRouteMatch();
  const history = useHistory();
  const { nameId } = useParams();

  const { isError: isProjectError, data, runtimeFields } = useProjectById(nameId, account?.accountId);
  const { isError: isMediaError, icon, filesList } = useProjectMediaFiles(data);
  const { isError: isProgressError, stageProgress, stageProgressHandler } = useStageProgress(data, runtimeFields);
  const [isLoading, setIsLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [isProjectFinished, setIsProjectFinished] = useState(false);
  const isError = isProjectError || isMediaError || isProgressError || showRequestModal;
  const initFunction = async () => {
    const { stages, status } = data;

    if (stages?.length && status) {
      const lastStageEndTime = getLastStageEndTime(stages);
      const isOver = isProjectOver(status.toLowerCase(), lastStageEndTime);
      setIsProjectFinished(isOver);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isError) setIsLoading(false);
  }, [isError]);

  useEffect(() => {
    if (data && runtimeFields) initFunction();
  }, [data, runtimeFields]);

  if (isLoading) {
    return (
      <div className="dashboard__loader">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <RequestModal
        error
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.oops' })}
        description={(
          <FormattedMessage
            id="modal.description.goesWrong"
            values={{
              link: (...chunks) => <a href="mailto:support@openforestprotocol.org" className="text-anchor">{chunks}</a>,
            }}
          />
)}
      />
    );
  }

  if (!data?.status) {
    return (
      <RequestModal
        error
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.wentWrong' })}
        description={translate.formatMessage({ id: 'modal.description.canNotTrack' })}
        buttonText={translate.formatMessage({ id: 'modal.button.return' })}
      />
    );
  }

  return (
    <div className="project__item">
      <div className="project__item-wrapper">
        <ProjectHeader name={data.name} projectId={data.project_id} />

        {/* ProjectPage navigation */}
        <ProjectTabs />

        {/* Nested ProjectPage routes */}
        <Switch>
          <Route exact path={`${path}`}>
            <ProjectView
              data={data}
              runtimeFields={runtimeFields}
              stageProgress={stageProgress}
              stageProgressHandler={stageProgressHandler}
              icon={icon}
              filesList={filesList}
              isProjectFinished={isProjectFinished}
            />
          </Route>

          <Route exact path={`${path}/field-agents`}>
            <ProjectFieldAgents
              data={data}
              setShowRequestModal={setShowRequestModal}
              isProjectFinished={isProjectFinished}
            />
          </Route>

          <Route path={`${path}/monitoring`}>
            <MonitoringData
              stagesInfo={data.stagesInfo}
              currentStage={getMapping(runtimeFields.currentStage)}
              isProjectFinished={isProjectFinished}
              projectStatus={data?.status}
            />
          </Route>

          <Route path={`${path}/carbon`}>
            <CarbonPage
              projectData={data}
              stagesInfo={data.stagesInfo}
              currentStage={getMapping(runtimeFields.currentStage)}
              isProjectFinished={isProjectFinished}
              projectStatus={data?.status}
            />
          </Route>

        </Switch>
      </div>
    </div>
  );
};

export default ProjectPage;
